import React, { useState, useEffect } from 'react';
import { 
  Box, 
  IconButton, 
  Tooltip, 
  Menu, 
  MenuItem, 
  Typography,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Paper,
  Fade,
  Grow,
  Divider,
  ButtonBase,
  alpha,
  Chip,
  Button
} from '@mui/material';
import { useLanguage, Language } from '../context/LanguageContext';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Flag SVG components
const UKFlag = () => (
  <SvgIcon viewBox="0 0 60 30" sx={{ width: 24, height: 18 }}>
    <clipPath id="t">
      <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/>
    </clipPath>
    <path d="M0,0 v30 h60 v-30 z" fill="#00247d"/>
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
    <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#cf142b" strokeWidth="4"/>
    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
    <path d="M30,0 v30 M0,15 h60" stroke="#cf142b" strokeWidth="6"/>
  </SvgIcon>
);

const FranceFlag = () => (
  <SvgIcon viewBox="0 0 30 20" sx={{ width: 24, height: 18 }}>
    <rect width="30" height="20" fill="#ED2939"/>
    <rect width="20" height="20" fill="#fff"/>
    <rect width="10" height="20" fill="#002395"/>
  </SvgIcon>
);

const GermanyFlag = () => (
  <SvgIcon viewBox="0 0 30 18" sx={{ width: 24, height: 18 }}>
    <rect width="30" height="18" fill="#000"/>
    <rect width="30" height="12" fill="#DD0000"/>
    <rect width="30" height="6" fill="#FFCE00"/>
  </SvgIcon>
);

// Map language codes to flag components
const FLAGS: Record<Language, React.ReactNode> = {
  en: <UKFlag />,
  fr: <FranceFlag />,
  de: <GermanyFlag />,
};

// Map language codes to full names
const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
};

// Map language codes to native names
const NATIVE_NAMES: Record<Language, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
};

// Map language codes to country/language identifiers
const LANGUAGE_LOCALE: Record<Language, string> = {
  en: 'EN-US',
  fr: 'FR-FR',
  de: 'DE-DE',
};

const LanguageSelector: React.FC<{ scrolled?: boolean }> = ({ scrolled = false }) => {
  const { language, setLanguage, t, countryCode, setManualCountryCode } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredLanguage, setHoveredLanguage] = useState<Language | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(language);
  const [animateFlag, setAnimateFlag] = useState(false);
  const [languageDetected, setLanguageDetected] = useState(false);
  const open = Boolean(anchorEl);

  // Synchronize selected language with context
  useEffect(() => {
    setSelectedLanguage(language);
  }, [language]);

  // Vérifier si la langue a été détectée automatiquement
  useEffect(() => {
    console.log("LanguageSelector - countryCode:", countryCode);
    console.log("LanguageSelector - language:", language);
    
    // Si le code pays est défini, la langue a probablement été détectée automatiquement
    if (countryCode) {
      // Vérifie si la langue est celle attendue pour le pays
      const savedLanguage = localStorage.getItem('language');
      console.log("LanguageSelector - savedLanguage:", savedLanguage);
      
      // Si aucune langue n'a été sauvegardée manuellement par l'utilisateur
      if (!savedLanguage || savedLanguage === language) {
        console.log("LanguageSelector - Langue détectée automatiquement");
        setLanguageDetected(true);
      } else {
        console.log("LanguageSelector - Langue définie manuellement");
        setLanguageDetected(false);
      }
    }
  }, [countryCode, language]);

  // Animation for flag when language changes
  useEffect(() => {
    if (selectedLanguage !== language) {
      setAnimateFlag(true);
      const timer = setTimeout(() => {
        setAnimateFlag(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [selectedLanguage, language]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setHoveredLanguage(null);
  };

  const handleLanguageChange = (newLanguage: Language) => {
    setSelectedLanguage(newLanguage);
    setLanguage(newLanguage);
    setLanguageDetected(false); // Une fois que l'utilisateur change manuellement la langue
    handleClose();
  };

  const handleMouseEnter = (lang: Language) => {
    setHoveredLanguage(lang);
  };

  const handleMouseLeave = () => {
    setHoveredLanguage(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={t('language.select') || "Changer de langue"} arrow placement="bottom">
        <ButtonBase
          onClick={handleClick}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            borderRadius: 8,
            overflow: 'hidden',
            px: 1.5,
            py: 0.75,
            transition: 'all 0.3s ease',
            bgcolor: open ? alpha('#0288d1', 0.08) : 'transparent',
            '&:hover': {
              bgcolor: alpha('#0288d1', 0.12),
            },

          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1
            }}
          >
            <Grow in={!animateFlag} timeout={300}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                p: 0.5,
                borderRadius: '50%',
                bgcolor: open ? alpha('#0288d1', 0.12) : 'transparent',
                transition: 'all 0.3s ease',
              }}>
                {FLAGS[language]}
              </Box>
            </Grow>
            
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'text.primary',
                fontSize: '0.75rem',
                ml: 0.5
              }}
            >
              {LANGUAGE_LOCALE[language]}
            </Typography>
            
            {languageDetected && (
              <Tooltip title="Langue détectée automatiquement selon votre localisation">
                <LocationOnIcon
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    opacity: 0.7,
                    ml: 0.5
                  }}
                />
              </Tooltip>
            )}
            
            <KeyboardArrowDownIcon 
              sx={{ 
                fontSize: '1rem', 
                ml: 0,
                transition: 'transform 0.3s ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                color: open ? 'primary.main' : 'text.primary',
                opacity: open ? 1 : 0.7
              }} 
            />
          </Box>
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 4,
          sx: {
            borderRadius: 2,
            minWidth: 220,
            overflow: 'visible',
            mt: 1.5,
            py: 1,
            '& .MuiList-root': {
              py: 0.5,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 18,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              boxShadow: '-2px -2px 3px rgba(0,0,0,0.05)'
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        TransitionComponent={Fade}
        transitionDuration={250}
      >
        <Box sx={{ px: 2, py: 1.5, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TranslateIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
              {t('language.select') || "Changer de langue"}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('language.choose') || "Choisissez votre langue préférée"}
          </Typography>
          
          {languageDetected && countryCode && (
            <Chip
              icon={<LocationOnIcon fontSize="small" />}
              label="Détecté automatiquement"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ 
                mt: 1.5, 
                fontSize: '0.7rem', 
                height: 22,
                '& .MuiChip-icon': { fontSize: '0.75rem' }
              }}
            />
          )}
        </Box>
        
        <Divider sx={{ mx: 2, mb: 1 }} />

        {/* Section de géolocalisation - toujours affichée */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.primary" fontWeight={600} sx={{ mb: 1 }}>
            {t('geolocation.title') || "Informations de localisation"}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('geolocation.detected') || "Pays détecté"}:
            </Typography>
            <Chip 
              label={countryCode || "Non détecté"} 
              size="small" 
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: countryCode ? 'background.default' : 'error.light',
                color: countryCode ? 'text.primary' : 'common.white'
              }} 
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('geolocation.defaultLanguage') || "Langue par défaut"}:
            </Typography>
            <Chip 
              icon={languageDetected ? <LocationOnIcon sx={{ fontSize: '0.75rem !important' }} /> : undefined}
              label={NATIVE_NAMES[language]} 
              size="small"
              color={languageDetected ? "primary" : "default"}
              variant={languageDetected ? "outlined" : "filled"}
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                fontWeight: 600,
                '& .MuiChip-icon': { 
                  fontSize: '0.75rem',
                  mr: '2px'  
                }
              }} 
            />
          </Box>
          
          {/* Debug - Affiche les valeurs pour le débogage */}
          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
              countryCode: {countryCode || 'null'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
              languageDetected: {languageDetected ? 'true' : 'false'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
              language: {language}
            </Typography>
            
            {/* Boutons pour définir manuellement le code pays pour test */}
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setManualCountryCode('FR')}
                sx={{ minWidth: 0, fontSize: '0.6rem', py: 0, px: 1, height: 20 }}
              >
                FR
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setManualCountryCode('DE')}
                sx={{ minWidth: 0, fontSize: '0.6rem', py: 0, px: 1, height: 20 }}
              >
                DE
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setManualCountryCode('US')}
                sx={{ minWidth: 0, fontSize: '0.6rem', py: 0, px: 1, height: 20 }}
              >
                US
              </Button>
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ mx: 2, my: 1 }} />
        
        {Object.entries(FLAGS).map(([langCode, flag]) => {
          const isSelected = language === langCode;
          const isHovered = hoveredLanguage === langCode;
          
          return (
            <MenuItem
              key={langCode}
              onClick={() => handleLanguageChange(langCode as Language)}
              onMouseEnter={() => handleMouseEnter(langCode as Language)}
              onMouseLeave={handleMouseLeave}
              sx={{
                py: 1,
                px: 2,
                borderRadius: 1,
                mx: 0.5,
                bgcolor: isSelected ? alpha('#0288d1', 0.08) : 'transparent',
                '&:hover': {
                  bgcolor: alpha('#0288d1', 0.12),
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {flag}
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? 'primary.main' : 'text.primary',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {NATIVE_NAMES[langCode as Language]}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        ml: 1, 
                        color: 'text.secondary',
                        opacity: isHovered ? 1 : 0.7,
                        transition: 'opacity 0.2s ease',
                      }}
                    >
                      {LANGUAGE_NAMES[langCode as Language]}
                    </Typography>
                  </Box>
                }
              />
              {isSelected && (
                <CheckIcon 
                  fontSize="small" 
                  color="primary" 
                  sx={{ ml: 1 }} 
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default LanguageSelector; 